import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SummaryApi from '../common';

// ================= THEME (UI rules) =================
const THEME = {
  primary: 'rgb(23, 44, 87)', // #172C57 — main brand color
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionBlue: '#2563EB',
  actionBlueHover: '#1D4ED8',
  actionYellow: '#D97706',
  actionYellowHover: '#B45309',
  actionGreen: '#10B981',
  actionGreenHover: '#059669',
  actionPurple: '#7C3AED',
  actionPurpleHover: '#6D28D9',
  actionRed: '#EF4444',
  actionRedHover: '#DC2626',
  disabledGray: '#D1D5DB',
};

const EmpTeamLead = () => {
  const [followupData, setFollowupData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [selectedCollege, setSelectedCollege] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedInterestStatus, setSelectedInterestStatus] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);

  const navigate = useNavigate();

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  // Moved out of useEffect so the Retry button can call it again (logic unchanged)
  const fetchFollowupData = async () => {
    try {
      setLoading(true);
      setError(null);

      const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
      if (!encodedEmployeeId) {
        throw new Error('Employee ID not found in session storage. Please log in again.');
      }

      const decodedEmployeeId = atob(encodedEmployeeId);
      const url = SummaryApi.getTlFollowupCountByEmpId.url;

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ employee_id: decodedEmployeeId }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      if (result.status) {
        setFollowupData(result.data);
        setFilteredData(result.data);
      } else {
        setError('Failed to fetch data: ' + (result.message || 'Unknown error'));
      }
    } catch (e) {
      console.error("Error fetching Team Lead Follow-up data:", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowupData();
  }, []);

  // ✅ Filter records (only include students with > 0 follow-ups)
  useEffect(() => {
    let filtered = followupData;

    // ✅ Only show students with more than 0 follow-ups
    filtered = filtered.filter(item => parseInt(item.followup_count) > 0);

    if (selectedCollege) {
      filtered = filtered.filter(item => item.followup_clg_name === selectedCollege);
    }

    if (selectedDistrict) {
      filtered = filtered.filter(item => item.followup_district === selectedDistrict);
    }

    if (selectedInterestStatus !== 'All') {
      filtered = filtered.filter(item => item.followup_is_interested === selectedInterestStatus);
    }

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        (item.student_name && item.student_name.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (item.followup_enrollment_code && item.followup_enrollment_code.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (item.student_mobile && item.student_mobile.includes(lowerCaseSearchTerm))
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [selectedCollege, selectedDistrict, selectedInterestStatus, searchTerm, followupData]);

  const uniqueColleges = [...new Set(followupData.map(item => item.followup_clg_name).filter(Boolean))];
  const uniqueDistricts = [...new Set(followupData.map(item => item.followup_district).filter(Boolean))];

  const interestStatuses = ['All', 'Most Interested', 'Interested'];

  // Status chips use semantic colors
  const getInterestColorClass = (interestLevel) => {
    switch (interestLevel) {
      case 'Most Interested':
        return 'bg-green-100 text-green-700';
      case 'Interested':
        return 'bg-yellow-100 text-yellow-700';
      case 'Moderately Interested':
        return 'bg-amber-100 text-amber-700';
      case 'Less Interested':
        return 'bg-orange-100 text-orange-700';
      case 'Not Interested':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleRowClick = (studentId) => {
    const encodedStudentId = btoa(studentId.toString());
    navigate(`/emp-team-lead-history/${encodedStudentId}`);
  };

  // Shared style helpers
  const inputClass =
    'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition ' +
    'placeholder:text-gray-400 hover:border-gray-400 focus:border-[#172C57] focus:ring-2 focus:ring-[#172C57]';
  const labelClass = 'mb-1 block text-sm font-semibold';
  const labelStyle = { color: THEME.primary };

  if (loading) {
    return (
      <div className="min-h-screen w-full">
        <div className="w-full space-y-5">
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: THEME.primary }}>
            Team Lead Follow-up
          </h1>
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-6 py-16 shadow-sm">
            <div
              className="h-10 w-10 animate-spin rounded-full border-4 border-t-transparent"
              style={{ borderColor: THEME.primary, borderTopColor: 'transparent' }}
            />
            <p className="text-sm text-gray-500">Loading Team Lead Follow-up Data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full">
        <div className="w-full space-y-5">
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: THEME.primary }}>
            Team Lead Follow-up
          </h1>
          <div className="mx-auto flex max-w-lg flex-col items-center gap-3 rounded-xl border border-red-100 bg-white px-6 py-12 text-center shadow-sm">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold" style={{ color: THEME.primary }}>
              Something went wrong
            </h2>
            <p className="text-sm text-red-600">{error}</p>
            <p className="text-sm text-gray-500">
              We couldn't load the follow-up data. Check your connection and try again.
            </p>
            <button
              type="button"
              onClick={fetchFollowupData}
              style={{ backgroundColor: THEME.primary }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              className="mt-2 rounded-lg px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full">
      <div className="w-full">
        <h1 className="text-2xl md:text-3xl font-bold" style={{ color: THEME.primary }}>
          Team Lead Follow-up
        </h1>

        {/* Filters */}
        <div className="flex flex-col justify-between gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:flex-row md:items-end">
          {/* Left Section */}
          <div className="flex w-full flex-col gap-4 md:w-auto">
            <div className="flex flex-wrap gap-2">
              {interestStatuses.map((status) => (
                <button
                  key={status}
                  onClick={() => setSelectedInterestStatus(status)}
                  style={selectedInterestStatus === status ? { backgroundColor: THEME.primary } : undefined}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition
                    ${selectedInterestStatus === status
                      ? 'text-white shadow-sm'
                      : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  {status} ({status === 'All'
                    ? filteredData.length
                    : filteredData.filter(item => item.followup_is_interested === status).length
                  })
                </button>
              ))}
            </div>

            <div className="w-full md:w-96">
              <input
                type="text"
                placeholder='Search by mobile number, student name, enrollment code'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Right Section */}
          <div className="flex w-full flex-wrap gap-4 md:w-auto md:justify-end">
            <div className="w-full sm:w-52">
              <label className={labelClass} style={labelStyle}>Filter by College</label>
              <select
                value={selectedCollege}
                onChange={(e) => setSelectedCollege(e.target.value)}
                className={inputClass}
              >
                <option value="">All Colleges</option>
                {uniqueColleges.map((clg, idx) => (
                  <option key={idx} value={clg}>{clg}</option>
                ))}
              </select>
            </div>

            <div className="w-full sm:w-52">
              <label className={labelClass} style={labelStyle}>Filter by District</label>
              <select
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                className={inputClass}
              >
                <option value="">All Districts</option>
                {uniqueDistricts.map((dist, idx) => (
                  <option key={idx} value={dist}>{dist}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        {filteredData.length === 0 ? (
          <div className="mx-auto flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full"
              style={{ backgroundColor: THEME.primaryLight, color: THEME.primary }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold" style={{ color: THEME.primary }}>
              No follow-up data available
            </h2>
            <p className="text-sm text-gray-500">
              Try changing the filters or search term.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full">
                <thead className="bg-gray-200">
                  <tr>
                    {['S.no', 'Student Name', 'Mobile Number', 'Team Lead Name', 'Enrollment Code', 'District', 'City', 'College Name', 'Interest Level', 'Follow-up Count']
                      .map((header, idx) => (
                        <th
                          key={idx}
                          className="px-4 py-2 text-left font-semibold whitespace-nowrap"
                          style={{ color: THEME.primary }}
                        >
                          {header}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.map((student, index) => (
                    <tr
                      key={student.student_id}
                      className="cursor-pointer border-b border-slate-100 transition hover:bg-slate-50"
                      onClick={() => handleRowClick(student.student_id)}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {indexOfFirstRecord + index + 1}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-[150px] truncate hover:whitespace-normal" title={student.student_name}>
                        {student.student_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{student.mobile_number1 || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{student.team_lead_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{student.followup_enrollment_code}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{student.followup_district}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{student.followup_city}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-[200px] truncate hover:whitespace-normal" title={student.followup_clg_name}>
                        {student.followup_clg_name}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getInterestColorClass(student.followup_is_interested)}`}>
                          {student.followup_is_interested}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${parseInt(student.followup_count) > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                          {student.followup_count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
                <span className="text-sm text-gray-600">
                  Total records:{' '}
                  <span className="font-semibold" style={{ color: THEME.primary }}>
                    {filteredData.length}
                  </span>
                </span>

                <div className="flex items-center gap-2">
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-[#172C57] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    Previous
                  </button>
                  <span
                    className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
                    style={{ backgroundColor: THEME.primary }}
                  >
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-[#172C57] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmpTeamLead;